import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { useTheme } from '@/app/ThemeContext';
import { Loader } from '@/components/Loader';
import { useAllClientPluginUsage } from '@/sections/customerManagement/components/masterDashboard/hooks/useClientPluginUsage';
import { useEnrichmentPlugins } from '@/sections/customerManagement/components/masterDashboard/hooks/useEnrichmentPlugins';
import { LimitConfigModal } from '@/sections/customerManagement/components/masterDashboard/LimitConfigModal';

interface ClientBudgetTabProps {
  client: string;
}

export const ClientBudgetTab: React.FC<ClientBudgetTabProps> = ({
  client,
}) => {
  const { currentTheme } = useTheme();
  const { data: plugins, status: pluginsStatus } = useEnrichmentPlugins();
  const pluginIds = (plugins ?? []).map(p => p.plugin);
  const { data: usageData, status: usageStatus } = useAllClientPluginUsage(
    client,
    pluginIds,
    pluginsStatus === 'success'
  );

  const [configPlugin, setConfigPlugin] = useState<string | null>(null);

  if (pluginsStatus === 'pending' || usageStatus === 'pending') {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  if (pluginsStatus === 'error' || usageStatus === 'error') {
    return (
      <p className="text-chariot-text-secondary py-8 text-center text-sm">
        Failed to load usage data. Try refreshing the page.
      </p>
    );
  }

  const pluginNameMap = new Map(
    (plugins ?? []).map(p => [p.plugin, p.name])
  );

  return (
    <>
      <div className="flex flex-col gap-2">
        {(usageData ?? []).map(usage => {
          const pct =
            usage.limit > 0
              ? Math.round((usage.totalUsed / usage.limit) * 100)
              : 0;

          return (
            <div
              key={usage.plugin}
              className="border-chariot-stroke bg-chariot-background-primary flex items-center gap-4 rounded-lg border px-4 py-3"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <img
                      src={`/icons/${currentTheme}/${usage.plugin.replace('pius_', '')}.svg`}
                      alt={pluginNameMap.get(usage.plugin) ?? usage.plugin}
                      className="size-4 rounded"
                    />
                    <span className="text-chariot-text-primary text-sm font-medium">
                      {pluginNameMap.get(usage.plugin) ?? usage.plugin}
                    </span>
                  </div>
                  <span className="text-chariot-text-secondary text-xs">
                    {usage.totalUsed} / {usage.limit || '—'} credits
                  </span>
                </div>
                {usage.limit > 0 && (
                  <div className="bg-chariot-background-secondary h-1.5 w-full rounded-full">
                    <div
                      className="h-1.5 rounded-full bg-blue-500 transition-all"
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                )}
                {usage.lastRun && (
                  <span className="text-chariot-text-tertiary text-xs">
                    Last run: {new Date(usage.lastRun).toLocaleString()}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfigPlugin(usage.plugin)}
                className="text-chariot-text-tertiary hover:text-chariot-text-primary transition-colors"
                title="Configure limits"
              >
                <Cog6ToothIcon className="size-5" />
              </button>
            </div>
          );
        })}

        {(!usageData || usageData.length === 0) && (
          <p className="text-chariot-text-secondary py-8 text-center text-sm">
            No usage data available. Configure limits to start tracking.
          </p>
        )}
      </div>

      <LimitConfigModal
        key={configPlugin}
        client={client}
        plugin={configPlugin}
        pluginName={
          configPlugin ? (pluginNameMap.get(configPlugin) ?? configPlugin) : ''
        }
        onClose={() => setConfigPlugin(null)}
      />
    </>
  );
};
